import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '@/contexts/useOnboardingContext';
import { useAuth } from '@/contexts/useAuth';
import { updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import { CONCERN_OPTIONS } from '@/types/profile';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/components/ui/ErrorAlert';
import OnboardingShell from '@/components/layout/OnboardingShell';

export default function ConcernsPage() {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const { state, toggleConcern, setStep } = useOnboardingContext();
  const selected = state.nervous_about || [];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const handleContinue = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, { nervous_about: selected });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    setStep(5);
    await refreshProfile();
    track('onboarding_step_completed', { step: 'concerns' });
    navigate('/onboarding/value-preview');
  };

  return (
    <OnboardingShell
      step={4}
      title={<>What are you most nervous about?</>}
      subtitle={<>Select all that apply.</>}
    >
      <div className="space-y-3">
        {CONCERN_OPTIONS.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <button
              key={option.value}
              onClick={() => toggleConcern(option.value)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-colors flex items-center gap-3 cursor-pointer ${
                isSelected
                  ? 'border-primary bg-primary-light'
                  : 'border-border bg-card hover:border-primary/30'
              }`}
            >
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-border'}`}>
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span className="text-xl">{option.icon}</span>
              <span className="font-sans font-medium text-text-primary">{option.label}</span>
            </button>
          );
        })}
      </div>

      {error && <ErrorAlert error={error} className="mt-6" />}

      <div className="mt-8">
        <Button fullWidth onClick={handleContinue} disabled={saving}>
          {saving ? 'Saving…' : 'Continue'}
        </Button>
      </div>
    </OnboardingShell>
  );
}
